import { ClickOptions, Keys, TestElement, waitFor } from '@oracle/oraclejet-testing/UNSAFE_Driver';
import { byCss } from '@oracle/oraclejet-testing/UNSAFE_Locators';
import { TestAdapter } from '@oracle/oraclejet-testing/UNSAFE_TestAdapter';

export class TableViewAdapter extends TestAdapter {
  /**
   * Sort the table view by the column specified
   * @param columnKey The columnKey of the column
   * @returns Promise<void>
   */
  async doSort<C extends string>(columnKey: C) {
    const driver = this.getTestDriver();
    const tableView = await this.getElement();
    let header;
    try {
      header = await tableView.waitForElement(
        byCss(`[data-oj-cell-type="header"][data-oj-column-key="${columnKey}"]`)
      );
    } catch (_e) {
      throw new Error(`Specified column key ${columnKey} is invalid or does not exist`);
    }
    return driver.sendKeys(header, Keys['ENTER']);
  }

  /**
   * Triggers rowAction event
   * @param rowKey The rowKey
   * @returns Promise<void>
   */
  async doRowAction<K extends string | number>(rowKey: K) {
    const tableView = await this.getElement();
    let row;
    try {
      row = await tableView.waitForElement(byCss(`[data-oj-key="${rowKey}"]`));
    } catch (_e) {
      throw new Error(`Specified row key ${rowKey} is invalid or does not exist`);
    }
    return this._clickItem(row);
  }

  /**
   * Toggles row selection
   * @param rowKey The rowKey
   * @returns Promise<void>
   */
  async doToggleSelectRow<K extends string | number>(rowKey: K) {
    const tableView = await this.getElement();
    let row;
    try {
      row = await tableView.waitForElement(byCss(`[data-oj-key="${rowKey}"]`));
    } catch (_e) {
      throw new Error(`Specified row key ${rowKey} is invalid or does not exist`);
    }
    return this._clickItem(row, { modifiers: [Keys['CONTROL_COMMAND']] });
  }

  /**
   * Toggles column selection
   * @param columnKey The columnKey
   * @returns Promise<void>
   */
  async doToggleSelectColumn<C extends string>(columnKey: C) {
    const tableView = await this.getElement();
    let column;
    try {
      column = await tableView.waitForElement(
        byCss(`[data-oj-cell-type="header"][data-oj-column-key="${columnKey}"]`)
      );
    } catch (_e) {
      throw new Error(`Specified column key ${columnKey} is invalid or does not exist`);
    }
    return this._clickItem(column, { modifiers: [Keys['CONTROL_COMMAND']] });
  }

  /**
   * Perform a TestDriver click() on the item, but first ensure that it's scrolled into
   * view. TODO: this shouldn't be needed once JET-73795 is fixed.
   * @param item
   * @param options
   * @returns
   */
  private async _clickItem(item: TestElement, options?: ClickOptions) {
    const driver = this.getTestDriver();
    await driver.executeScript(
      (el: HTMLElement) => el.scrollIntoView({ block: 'center', inline: 'center' }),
      driver.unwrapElement(item)
    );
    return driver.click(item, options);
  }

  /**
   * Load more rows
   * @param locator The TestIdLocator which identifies the TableView
   * @returns Promise<void>
   */
  async doLoadMore() {
    const driver = this.getTestDriver();
    const tableView = await this.getElement();
    const rowCount = (await tableView.waitForElements(byCss('tr[role="row"]'))).length;
    await driver.sendKeys(tableView, Keys['CONTROL_COMMAND'], Keys['END']);
    await waitFor(async () => {
      return (
        (await tableView.waitForElements(byCss('tr[role="row"]'))).length != rowCount ||
        (await tableView.waitForElements(byCss('[data-oj-cell-type="loadMore"]'))).length === 0
      );
    });
  }
}
